import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import MainLayout from '@/components/layout/MainLayout';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { MapPin, Phone } from "lucide-react";
import SampleCountCards from '@/components/assign-sample/SampleCountCards';
import AssignFilters from '@/components/assign-sample/AssignFilters';
import { AssignFilters as AssignFiltersType } from '@/hooks/assign-sample/useAssignFilters';
import { updateAccommodationSampleType } from '@/features/alojamientos/api/accommodation.api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fetchSampleTypes, SampleType } from '@/features/alojamientos/services/sampleTypeService';
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useRegions } from '@/features/alojamientos/hooks/useRegions';
import { useLocalities } from '@/features/alojamientos/hooks/useLocalities';
import { useSegments } from '@/features/alojamientos/hooks/useSegments';
import { useStatusOptions } from '@/features/alojamientos/hooks/useStatusOptions';
import { useClassificationOptions } from '@/features/alojamientos/hooks/useClassificationOptions';
import { useAssignFilters } from '@/hooks/assign-sample/useAssignFilters';
import EstablishmentRow from '@/components/assign-sample/EstablishmentRow';
import { Accommodation } from '@/features/alojamientos/types/accomodation.types';
import eventBus from '@/utils/eventBus';
interface Establishment {
  id: number;
  name: string;
  establishmentName: string;
  classId: string;
  localityId: string;
  region: string;
  phone: string;
  whatsapp: string;
  observations: string;
  statusId: string;
  capacity: number;
  sampleTypeId?: number;
}

const AssignSample = () => {
  const [sampleTypes, setSampleTypes] = useState<SampleType[]>([]);
  const [selectedMap, setSelectedMap] = useState<Record<number, boolean>>({});
  const originalSampleTypeIdsRef = useRef<Set<number>>(new Set());

  const { regions } = useRegions();
  const { localities } = useLocalities();
  const { segments } = useSegments();
  const { statusOptions } = useStatusOptions();
  const { typeOptions } = useClassificationOptions(null);

  const { toast } = useToast();
  const selectedSampleIdRef = useRef<string>('');
  
  // Usar el hook optimizado de useAssignFilters


  const [filteredResults, setFilteredResults] = useState<any[]>([]);
  const [filtersReady, setFiltersReady] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(30);
  const [paginatedResults, setPaginatedResults] = useState<any[]>([]);

  useEffect(() => {
    const handler = (newResults: any) => {
      setFilteredResults(newResults);
      //setCurrentPage(1); // Resetear a la primera página
      const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const newPaginatedResults = filteredResults.slice(startIndex, endIndex);
    setPaginatedResults(newPaginatedResults);

    const maxPage = Math.max(1, Math.ceil(filteredResults.length / itemsPerPage));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }

    if(selectedSampleIdRef.current!==""){
       
      const establishmentsWithSampleType = newResults.filter(est => 
      est.sampleTypeId?.toString() === selectedSampleIdRef.current.toString()
    );
    originalSampleTypeIdsRef.current = new Set(establishmentsWithSampleType.map(est => (est.id)));
  
    // Seleccionar todos los establecimientos que tienen o tendrán este tipo de muestra
    const newSelectedMap = newResults.reduce((acc, est) => ({
      ...acc,
      [est.id]: est.sampleTypeId?.toString() === selectedSampleIdRef.current.toString()
    }), {});
    
    setSelectedMap(newSelectedMap);
    }
    loadSampleTypes();

    };
    eventBus.on('newResults', handler);
    return () => {
      eventBus.off('newResults', handler);
    };
    
  }, []);

  // Efecto para actualizar paginatedResults cuando cambian los filtros o la paginación
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const newPaginatedResults = filteredResults.slice(startIndex, endIndex);
    setPaginatedResults(newPaginatedResults);

    const maxPage = Math.max(1, Math.ceil(filteredResults.length / itemsPerPage));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [filteredResults, itemsPerPage, currentPage]);

  // Calcular el número total de páginas
  const totalPages = Math.ceil(filteredResults.length / itemsPerPage);

  // Manejadores para la paginación
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Resetear a la primera página
  };

  // Efecto para monitorear renderizados y cambios de estado
  useEffect(() => {
    const renderStart = performance.now();
    return () => {
      const renderEnd = performance.now();
    };
  }, [sampleTypes, selectedMap, selectedSampleIdRef, filteredResults]);

  const loadSampleTypes = async () => {
    try {
     // setIsLoadingSampleTypes(true);
      const types = await fetchSampleTypes();
      setSampleTypes(types);
    } catch (error) {
      console.error('❌ Error al cargar los tipos de muestra:', error);
    } finally {
     // setIsLoadingSampleTypes(false);
    }
  };

  useEffect(() => {
    loadSampleTypes();
  }, []);

  const handleAssignmentChange = useCallback((establishmentId: number, value: string) => {    
  }, []);

  const handleRowSelection = (establishmentId: number, checked: boolean) => {
    setSelectedMap(prev => ({
      ...prev,
      [establishmentId]: checked
    }));
  };

  // Agregar useEffect para monitorear cambios en selectedMap
  useEffect(() => {
  }, [selectedMap]);

  // Agregar useEffect para monitorear el renderizado de cada fila
  useEffect(() => {
  }, [filteredResults]);

  const handleSelectAll = (checked: boolean) => {
    if(checked){
      eventBus.emit('sampleIdChange', "all");
      const newSelectedMap = filteredResults.reduce((acc, est) => ({
        ...acc,
        [est.id]: checked
      }), {});
      
      setSelectedMap(newSelectedMap);
    }else if(!checked && selectedSampleIdRef.current!==""){
      eventBus.emit('sampleIdChange',  selectedSampleIdRef.current);
      const establishmentsWithSampleType = filteredResults.filter(est => 
      est.sampleTypeId?.toString() === selectedSampleIdRef.current.toString()
    );
    originalSampleTypeIdsRef.current = new Set(establishmentsWithSampleType.map(est => (est.id)));
  
    // Seleccionar todos los establecimientos que tienen o tendrán este tipo de muestra
    const newSelectedMap = filteredResults.reduce((acc, est) => ({
      ...acc,
      [est.id]: est.sampleTypeId?.toString() === selectedSampleIdRef.current.toString()
    }), {});
    
    setSelectedMap(newSelectedMap);
    }else{
      eventBus.emit('sampleIdChange', "");

      const newSelectedMap = filteredResults.reduce((acc, est) => ({
        ...acc,
        [est.id]: checked
      }), {});
      
      setSelectedMap(newSelectedMap);
    }
   
    
    console.timeEnd('handleSelectAll-total');
  };

  const handleSelectBySampleType = useCallback((sampleTypeId: string) => {
    selectedSampleIdRef.current = sampleTypeId;
    
    eventBus.emit('sampleIdChange', sampleTypeId);
    
    // Guardar los IDs originales de los establecimientos que tenían este tipo de muestra
    const establishmentsWithSampleType = filteredResults.filter(est => 
      est.sampleTypeId?.toString() === sampleTypeId
    );
    originalSampleTypeIdsRef.current = new Set(establishmentsWithSampleType.map(est => (est.id)));
  
    // Seleccionar todos los establecimientos que tienen o tendrán este tipo de muestra
    const newSelectedMap = filteredResults.reduce((acc, est) => ({
      ...acc,
      [est.id]: est.sampleTypeId?.toString() === sampleTypeId
    }), {});
    
    setSelectedMap(newSelectedMap);
  }, [filteredResults]);

  const handleSave = useCallback(async () => {
    try {
      // Encontrar establecimientos que necesitan actualización
      const establishmentsToUpdate = filteredResults.filter(est => {
        const wasInSampleType = originalSampleTypeIdsRef.current.has(est.id);
        const isNowSelected = selectedMap[est.id] || false;
        
        // Solo actualizar si el estado cambió
        return wasInSampleType !== isNowSelected;
      });


      // Procesar actualizaciones de forma secuencial
      for (const est of establishmentsToUpdate) {
        console.time(`updateEstablishment-${est.id}`);
        try {
          const sampleTypeId = selectedMap[est.id] ? Number(selectedSampleIdRef.current) : null;
          await updateAccommodationSampleType(est.id.toString(), { sampleTypeId });
          console.timeEnd(`updateEstablishment-${est.id}`);
        } catch (error) {
          console.error(`Error al actualizar establecimiento ${est.id}:`, error);
          throw error;
        }
      }

      eventBus.emit('update', "");
     
      toast({
        title: "Asignaciones guardadas",
        description: `Se han actualizado ${establishmentsToUpdate.length} establecimientos`,
      });

      //setSelectedMap({});
      //selectedSampleIdRef.current = "";
      //eventBus.emit('sampleIdChange', "");
      //originalSampleTypeIdsRef.current = new Set();
    } catch (error) {
      console.error('❌ Error al guardar las asignaciones:', error);
      toast({
        title: "Error",
        description: "Ocurrió un error al guardar las asignaciones",
        variant: "destructive"
      });
    } finally {
      console.timeEnd('handleSave');
    }
  }, [filteredResults, selectedMap, originalSampleTypeIdsRef, toast]);

  const allSelected = filteredResults.length > 0 && 
    filteredResults.every(est => selectedMap[est.id]);
  const someSelected = Object.values(selectedMap).some(Boolean);

  // Agregar useEffect para monitorear cambios en los estados principales
  useEffect(() => {
  }, [sampleTypes, selectedMap, filteredResults]);

  // Función para verificar si todos los datos necesarios están disponibles
  const areAllDataAvailable = useCallback(() => {
    return (
      regions.length > 0 &&
      localities.length > 0 &&
      segments.length > 0 &&
      statusOptions.length > 0 &&
      sampleTypes.length > 0
    );
  }, [regions, localities, segments, statusOptions, sampleTypes]);

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Configurar Muestras</h1>
            <p className="text-gray-600 mt-2">
              Asigna muestras a alojamientos
            </p>
          </div>
          <Button 
            onClick={handleSave} 
            size="lg"
            //disabled={selectedSampleIdRef.current === ''}
          >
            Guardar
          </Button>
        </div>
          <SampleCountCards 
          sampleTypes={sampleTypes}
          onSelectBySampleType={handleSelectBySampleType}
          selectedSampleId={ selectedSampleIdRef.current}
        />

          <AssignFilters 
            onFiltersChange={null}
            regions={regions}
            localities={localities}
            segments={segments}
            statusOptions={statusOptions}
            sampleTypes={sampleTypes}
            onDataReady={setFiltersReady}
          />

        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>
                Alojamientos 
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({filteredResults.length} establecimiento{filteredResults.length !== 1 ? 's' : ''})
                </span>
                {someSelected && (
                  <span className="text-sm font-normal text-blue-600 ml-2">
                    ({Object.values(selectedMap).filter(Boolean).length} seleccionado{Object.values(selectedMap).filter(Boolean).length !== 1 ? 's' : ''})
                  </span>
                )}
              </CardTitle>
              <div className="flex items-center gap-4">
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(value) => handleItemsPerPageChange(Number(value))}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Elementos por página" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 por página</SelectItem>
                    <SelectItem value="30">30 por página</SelectItem>
                    <SelectItem value="50">50 por página</SelectItem>
                    <SelectItem value="100">100 por página</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table className="border border-gray-200">
                <TableHeader className="bg-gray-100">
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={handleSelectAll}
                        aria-label="Seleccionar todos"
                      />
                    </TableHead>
                    <TableHead>Nombre Alojamiento</TableHead>
                    <TableHead>Tipo/Clase</TableHead>
                    <TableHead>Localidad</TableHead>
                    <TableHead>Región</TableHead>
                    <TableHead>Teléfono</TableHead>
                    <TableHead>WhatsApp</TableHead>
                    <TableHead>Observaciones</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Capacidad</TableHead>
                    <TableHead>Asignado a</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!filtersReady ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-4">
                        Cargando datos necesarios...
                      </TableCell>
                    </TableRow>
                  ) : filteredResults.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-4">
                        No se encontraron establecimientos
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedResults.map((establishment) => {
                      return (
                        <EstablishmentRow
                          key={establishment.id}
                          establishment={establishment}
                          isSelected={selectedMap[establishment.id] || false}
                          onSelect={handleRowSelection}
                          sampleTypes={sampleTypes}
                          typeOptions={typeOptions}
                          localities={localities}
                          statusOptions={statusOptions}
                          onAssignChange={handleAssignmentChange}
                          selectedSampleType={selectedSampleIdRef.current}
                        />
                      );
                    })
                  )}
                </TableBody>
              </Table>
              {filteredResults.length > 0 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-gray-500">
                    Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredResults.length)} de {filteredResults.length} resultados
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Anterior
                    </Button>
                    <div className="text-sm">
                      Página {currentPage} de {totalPages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

// Agregar log para el memo
//console.log('🔄 AssignSample - Componente memoizado');

export default AssignSample;

